import React, { useEffect, useMemo, useState } from 'react';
import { BadgeCheck, BarChart3, Briefcase, Building2, CalendarDays, CheckCircle2, LayoutDashboard, RefreshCw, TrendingUp, UserCheck, Users } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getDashboardAnalytics } from '../api';
import { User } from '../types';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
}

export default function DashboardPage({ currentUser, notificationBell }: Props) {
  const [range, setRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  const customActive = !!(customStart && customEnd && customStart <= customEnd);

  const load = async () => {
    try {
      setLoading(true);
      const result = await getDashboardAnalytics(
        customActive ? { start: customStart, end: customEnd } : { range }
      );
      setData(result);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '加载仪表盘失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [range, customStart, customEnd]);

  const pickRange = (v: string) => { setCustomStart(''); setCustomEnd(''); setRange(v); };

  const maxFunnelCount = useMemo(() => Math.max(...(data?.funnel || []).map((i: any) => i.count), 1), [data]);
  const trendItems = mergeTrend(data);
  const maxTrendCount = useMemo(() => Math.max(...trendItems.map((i: any) => Math.max(i.candidates, i.interviews)), 1), [trendItems]);
  const summaryCards = data ? [
    { label: '候选人总数', value: data.summary.total_candidates, icon: Users, sub: `新增 ${data.summary.new_candidates}` },
    { label: '面试场次', value: data.summary.total_interviews, icon: CalendarDays, sub: `完成 ${data.summary.completed_interviews}` },
    { label: '面试完成率', value: `${data.summary.interview_completion_rate}%`, icon: CheckCircle2, sub: `取消 ${data.summary.cancelled_interviews}` },
    { label: '入职综合评分', value: `${data.summary.onboarded_avg_score || 0}/10`, icon: TrendingUp, sub: `按${data.summary.onboarded_rated_count || 0}位入职候选人` },
    { label: 'Offer数', value: data.summary.offer_in_range, icon: Briefcase, sub: '区间内通过' },
    { label: '入职数', value: data.summary.onboarded_in_range, icon: UserCheck, sub: '按实际入职', emphasis: true },
    { label: '编制创建', value: data.summary.headcount_created, icon: Building2, sub: '区间内新建' },
    { label: '编制完成', value: data.summary.headcount_completed, icon: BadgeCheck, sub: '按实际入职', emphasis: true },
  ] : [];

  if (!isHr) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <Header notificationBell={notificationBell} />
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <p className="text-sm">当前角色暂无全局仪表盘权限</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header notificationBell={notificationBell}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
            {[
              { value: '7d', label: '近7天' },
              { value: '30d', label: '近30天' },
              { value: '90d', label: '近90天' },
            ].map(opt => (
              <button key={opt.value} onClick={() => pickRange(opt.value)}
                className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors', !customActive && range === opt.value ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className={clsx('flex items-center gap-1.5 px-2 py-1 rounded-xl border transition-colors', customActive ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white')}>
            <CalendarDays size={14} className={customActive ? 'text-primary-600' : 'text-gray-400'} />
            <input type="date" value={customStart} max={customEnd || undefined}
              onChange={e => setCustomStart(e.target.value)}
              className="text-xs text-gray-700 bg-transparent focus:outline-none" />
            <span className="text-xs text-gray-400">至</span>
            <input type="date" value={customEnd} min={customStart || undefined}
              onChange={e => setCustomEnd(e.target.value)}
              className="text-xs text-gray-700 bg-transparent focus:outline-none" />
            {customActive && (
              <button onClick={() => pickRange(range)}
                className="ml-0.5 text-[11px] text-gray-400 hover:text-primary-600">清除</button>
            )}
          </div>
          <button onClick={load} disabled={loading}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-primary-600 hover:border-primary-200 transition-colors disabled:opacity-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </Header>

      {loading && !data ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {summaryCards.map((card: any) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white rounded-2xl border border-gray-200 shadow-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', card.emphasis ? 'bg-primary-600' : 'bg-primary-50')}>
                      <Icon size={18} className={card.emphasis ? 'text-white' : 'text-primary-600'} />
                    </div>
                    <span className="text-[11px] text-gray-400">{card.sub}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{card.label}</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <section className="bg-white rounded-2xl border border-gray-200 p-5">
              <SectionTitle icon={BarChart3} title="招聘漏斗" subtitle="按候选人当前阶段统计" />
              <div className="mt-4 space-y-3">
                {(data?.funnel || []).filter((i: any) => i.count > 0).map((item: any) => (
                  <div key={item.stage}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-medium text-gray-700">{item.label}</span>
                      <span className="text-gray-400">{item.count} 人 · {item.percentage}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.max(item.count / maxFunnelCount * 100, 3)}%` }} />
                    </div>
                  </div>
                ))}
                {(data?.funnel || []).every((i: any) => i.count === 0) && <EmptyText text="暂无候选人阶段数据" />}
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-200 p-5">
              <SectionTitle icon={TrendingUp} title="数据趋势" subtitle="新增候选人与面试场次" />
              <div className="mt-5 h-56 flex items-end gap-1.5 border-b border-gray-100 px-1">
                {trendItems.map((item: any) => (
                  <div key={item.date} className="flex-1 h-full flex items-end gap-0.5" title={`${item.date} 候选人 ${item.candidates} 面试 ${item.interviews}`}>
                    <div className="flex-1 rounded-t bg-primary-500 min-h-[2px]" style={{ height: `${Math.max(item.candidates / maxTrendCount * 100, item.candidates ? 4 : 0)}%` }} />
                    <div className="flex-1 rounded-t bg-primary-200 min-h-[2px]" style={{ height: `${Math.max(item.interviews / maxTrendCount * 100, item.interviews ? 4 : 0)}%` }} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-primary-500" />新增候选人</span>
                <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-primary-200" />面试场次</span>
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <section className="bg-white rounded-2xl border border-gray-200 p-5">
              <SectionTitle icon={UserCheck} title="面试官表现" subtitle="按评价结果统计通过率" />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-medium">面试官</th>
                      <th className="py-2 font-medium text-right">评价</th>
                      <th className="py-2 font-medium text-right">通过率</th>
                      <th className="py-2 font-medium text-right">均分</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(data?.interviewer_stats || []).map((row: any) => (
                      <tr key={row.user_id}>
                        <td className="py-3">
                          <div className="font-medium text-gray-800">{row.name}</div>
                          <div className="text-[11px] text-gray-400">{roleLabel(row.role)}</div>
                        </td>
                        <td className="py-3 text-right text-gray-600">{row.evaluation_count}</td>
                        <td className="py-3 text-right">
                          <span className="font-semibold text-primary-600">{row.pass_rate}%</span>
                          <div className="text-[11px] text-gray-400">{row.passed_count} 过 / {row.rejected_count} 淘汰</div>
                        </td>
                        <td className="py-3 text-right font-semibold text-gray-800">{row.avg_rating || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(data?.interviewer_stats || []).length === 0 && <EmptyText text="暂无面试评价数据" />}
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-200 p-5">
              <SectionTitle icon={Briefcase} title="岗位招聘进度" subtitle="按岗位聚合当前候选人状态" />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-medium">岗位</th>
                      <th className="py-2 font-medium text-right">候选人</th>
                      <th className="py-2 font-medium text-right">面试中</th>
                      <th className="py-2 font-medium text-right">Offer</th>
                      <th className="py-2 font-medium text-right">入职</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(data?.position_stats || []).map((row: any) => (
                      <tr key={row.position_id}>
                        <td className="py-3">
                          <div className="font-medium text-gray-800">{row.position_name}</div>
                          <div className="text-[11px] text-gray-400">{row.status === 'open' ? '招聘中' : row.status === 'paused' ? '暂停' : '已关闭'} · 淘汰 {row.rejected_count}</div>
                        </td>
                        <td className="py-3 text-right font-semibold text-gray-800">{row.candidate_count}</td>
                        <td className="py-3 text-right text-gray-600">{row.interview_count}</td>
                        <td className="py-3 text-right text-gray-700">{row.offer_count}</td>
                        <td className="py-3 text-right font-semibold text-primary-600">{row.onboarded_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(data?.position_stats || []).length === 0 && <EmptyText text="暂无岗位数据" />}
              </div>
            </section>
          </div>

          <section className="bg-white rounded-2xl border border-gray-200 p-5">
            <SectionTitle icon={Users} title="招聘同学编制统计" subtitle="按岗位招聘负责人归集绑定编制；负责编制数为当前责任快照，完成数按所选时间段内实际入职时间统计" />
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                    <th className="py-2 font-medium">招聘负责人</th>
                    <th className="py-2 font-medium text-right">负责岗位</th>
                    <th className="py-2 font-medium text-right">负责编制数</th>
                    <th className="py-2 font-medium text-right">编制完成数</th>
                    <th className="py-2 font-medium text-right">完成率</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(data?.recruiter_stats || []).map((row: any) => {
                    const rate = row.headcount_responsible > 0
                      ? Math.round(row.headcount_completed * 1000 / row.headcount_responsible) / 10
                      : 0;
                    return (
                      <tr key={row.owner_id}>
                        <td className="py-3 font-medium text-gray-800">{row.owner_name}</td>
                        <td className="py-3 text-right text-gray-600">{row.position_count}</td>
                        <td className="py-3 text-right font-semibold text-gray-800">{row.headcount_responsible}</td>
                        <td className="py-3 text-right font-semibold text-primary-600">{row.headcount_completed}</td>
                        <td className="py-3 text-right text-gray-600">{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(data?.recruiter_stats || []).length === 0 && <EmptyText text="暂无招聘负责人编制数据" />}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function Header({ notificationBell, children }: { notificationBell?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
      <div className="min-w-0 pl-10 md:pl-0">
        <h1 className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <LayoutDashboard size={20} className="text-primary-600" />
          仪表盘
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">招聘数据概览与分析</p>
      </div>
      <div className="flex items-center gap-3">
        {children}
        {notificationBell}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div>
      <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
        <Icon size={16} className="text-primary-500" />
        {title}
      </h2>
      <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
    </div>
  );
}

function EmptyText({ text }: { text: string }) {
  return <div className="py-10 text-center text-sm text-gray-400">{text}</div>;
}

function roleLabel(role: string) {
  return role === 'admin' ? '管理员' : role === 'hr' ? 'HR' : role === 'vip' ? 'VIP面试官' : '面试官';
}

function mergeTrend(data: any) {
  const map: Record<string, { date: string; candidates: number; interviews: number }> = {};
  (data?.trends?.candidates || []).forEach((item: any) => {
    map[item.date] = { date: item.date, candidates: item.count, interviews: map[item.date]?.interviews || 0 };
  });
  (data?.trends?.interviews || []).forEach((item: any) => {
    map[item.date] = { date: item.date, candidates: map[item.date]?.candidates || 0, interviews: item.count };
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}
